import './HActivitiesFilterProvider.scss';

import * as tsx from 'vue-tsx-support';
import Vue, { VNodeData } from 'vue';
import { Component, Model, Prop } from 'vue-property-decorator';
import {
  ActivitiesFilterModel,
  createActivitiesFilterModel,
  FilterableActivity,
} from './schemes';
import {
  toFilterableActivities,
  activityPeriodFilter,
  activityTargetAgeFilter,
  activityTargetAgesFilter,
  activityKeywordsFilter,
} from './helpers';
import { HActivitiesFilterForm } from './HActivitiesFilterForm';
import { today, range } from '~/helpers';
import {
  Activity,
  ActivityTargetAge,
  ActivityCommons,
  ActivityTargetAgeOption,
  ActivitiesRequestResult,
} from '~/schemes';

export interface HActivitiesFilterProviderProps
  extends ActivitiesRequestResult {
  /** アクティビティ共通設定 */
  activityCommons: ActivityCommons;

  /** フィルタ条件 */
  value?: ActivitiesFilterModel;

  /** リストフィルタ結果変更検知時に自動スクロールさせる先の要素を取得するための関数 */
  autoScrollPosition?: () => Element | null | undefined;
}

export interface HActivitiesFilterProviderEmits {
  /** モデル値更新時 */
  onInput: ActivitiesFilterModel;
}

export interface HActivitiesFilterProviderScopedSlots {
  default: HActivitiesFilterProviderRef;
}

/**
 * アクティビティフィルタプロバイダ
 *
 * - アクティビティリストをフィルタするための仕組みを提供するプロバイダコンポーネント
 * - defaultスロットの中でプロバイダ機能に触れるのでいろいろやってください
 */
@Component<HActivitiesFilterProviderRef>({
  name: 'HActivitiesFilterProvider',
  provide() {
    return {
      activitiesFilterProvider: this,
    };
  },
  render() {
    const defaultSlot = this.$scopedSlots.default;
    return (
      <div staticClass="h-activities-filter-provider">
        {defaultSlot && defaultSlot(this)}
      </div>
    );
  },
  created() {
    this.filterConditions = this.value;
  },
  beforeDestroy() {
    // 自動スクロールタイマーを解除しておく
    this.clearAutoScrollToStartPositionTimer();
  },
  watch: {
    value() {
      this.filterConditions = this.value;
    },
  },
})
export class HActivitiesFilterProviderRef
  extends Vue
  implements HActivitiesFilterProviderProps {
  @Model('input', { type: Object, default: createActivitiesFilterModel })
  readonly value!: ActivitiesFilterModel;

  @Prop({ type: Object, required: true })
  readonly activityCommons!: ActivityCommons;

  @Prop({ type: Array, required: true })
  readonly matchedKeywords!: ActivitiesRequestResult['matchedKeywords'];

  @Prop({ type: Array, required: true }) readonly activities!: Activity[];
  @Prop(Function) readonly autoScrollPosition?: () =>
    | Element
    | null
    | undefined;

  /** ユーザーのフィルタ条件入力値 */
  filterConditions!: ActivitiesFilterModel;

  /**
   * 自動スクロールの実行を待機するタイマーID
   */
  private _autoScrollToStartPositionTimerId?: number;

  /**
   * 当日の開始時刻のDataインスタンス
   *
   * @see {@link today}
   */
  readonly today = today();

  /**
   * 選択中の期間（月）
   *
   * 1-12
   */
  get month() {
    return this.filterConditions.month;
  }

  /**
   * 選択中の対象年齢のリスト
   *
   * @see {@link ActivityTargetAge}
   */
  get targetAges() {
    return this.filterConditions.targetAges;
  }

  /**
   * 選択中のキーワードのリスト
   *
   * @see {@link ActivityTargetAge}
   */
  get keywords() {
    return this.filterConditions.keywords;
  }

  /**
   * フィルタ処理用に開催期間が拡張されたアクティビティ
   */
  get filterableActivities(): FilterableActivity[] {
    return toFilterableActivities(this.activities);
  }

  /**
   * 期間オプションリストの事前リスト
   *
   * * これはそのままは利用しない。アクティビティに存在しない期間で削ぎ落とす
   *
   * @see {@link periodOptions}
   */
  get months() {
    return [
      {
        name: this.$t('label.all') as string,
        value: null,
      },
      ...range(12, 1).map((month) => ({
        name: this.$t('label.month')[month - 1],
        value: String(month),
      })),
    ];
  }

  /**
   * 期間オプションリスト
   */
  get periodOptions() {
    const { filterableActivities: activities, today } = this;
    return this.months
      .filter(({ value }) => {
        return activities.some((activity) =>
          activityPeriodFilter(activity, value, today),
        );
      })
      .map(({ name, value }) => ({
        label: name,
        value,
      }));
  }

  /**
   * キーワードオプションリスト
   */
  get keywordOptions() {
    const { matchedKeywords } = this;

    return matchedKeywords.map(({ name, value }) => ({
      label: name || '',
      value,
    }));
  }

  /**
   * 対象年齢オプションリストの事前リスト
   *
   * * これはそのままは利用しない。アクティビティに存在しない期間で削ぎ落とす
   *
   * @see {@link periodOptions}
   */
  get targetAgeOptionsBase() {
    const options: ActivityTargetAgeOption[] = [
      // {
      //   name: this.$t('label.all') as string,
      //   value: null,
      //   range: null,
      // },
      ...this.$commons.getActivityTargetAges(),
    ];
    return options;
  }

  /**
   * 対象年齢オプションリスト
   */
  get targetAgeOptions() {
    const { filterableActivities: activities } = this;

    const formattedTargetList = this.targetAgeOptionsBase.filter((opt) => {
      // 「年齢制限なし」の場合はリターンする
      if (opt.value === '1') return false;
      const isMatchedActivity = activities.some((activity) =>
        activityTargetAgeFilter(activity, opt, false),
      );
      return isMatchedActivity;
    });

    return formattedTargetList.map(({ name, value }) => ({
      label: name,
      value,
    }));
  }

  /**
   * 選択中の対象年齢オプション
   */
  get selectedTargetAgeOptions(): ActivityTargetAgeOption[] {
    const { targetAges } = this;
    return this.targetAgeOptionsBase.filter((a) =>
      targetAges.includes(a.value as any),
    );
  }

  /**
   * フィルタ済みのアクティビティのリスト
   *
   * * フィルタといいつつ、ピン止めアクティビティのソートも一緒にやっている
   */
  get filteredActivities() {
    const { selectedTargetAgeOptions } = this;
    const { month } = this;
    const { filterableActivities: activities } = this;
    const { today } = this;

    const results = activities.filter((activity) => {
      return (
        activityPeriodFilter(activity, month, today) &&
        activityTargetAgesFilter(activity, selectedTargetAgeOptions) &&
        activityKeywordsFilter(activity, this.keywords)
      );
    });

    // ピン留めされているやつを一番上にソートする
    results.sort((a, b) => {
      const ap = a.pinned || false;
      const bp = b.pinned || false;
      if (ap === bp) return 0;
      if (ap) return -1;
      return 1;
    });
    return results;
  }

  /**
   * フィルタ済みのアクティビティリストの件数
   */
  get count() {
    return this.filteredActivities.length;
  }

  /**
   * 指定のキー＆値でモデル値を更新する
   *
   * @param key - キー
   * @param value - 値
   * @param autoScroll - リストに変更が発生した際に自動スクロールを行うか
   */
  setValue<Key extends keyof ActivitiesFilterModel>(
    key: Key,
    value: ActivitiesFilterModel[Key],
    autoScroll?: boolean,
  ) {
    const { filterConditions } = this;
    if (filterConditions[key] === value) return;
    const ids = this.createIds();
    filterConditions[key] = value;
    this.$emit('input', filterConditions);
    if (!autoScroll) return;
    const hasChanged = JSON.stringify(ids) !== JSON.stringify(this.createIds());
    hasChanged && this.reserveAutoScrollToStartPosition();
  }

  /**
   * モデル値を部分的に更新する
   * @param values - モデル値（部分的）
   * @param autoScroll - リストに変更が発生した際に自動スクロールを行うか
   */
  setValues(values: Partial<ActivitiesFilterModel>, autoScroll?: boolean) {
    const { filterConditions } = this;
    const ids = this.createIds();
    Object.entries(values).forEach(([key, value]) => {
      filterConditions[key] = value;
    });
    this.$emit('input', filterConditions);
    if (!autoScroll) return;
    const hasChanged = JSON.stringify(ids) !== JSON.stringify(this.createIds());
    hasChanged && this.reserveAutoScrollToStartPosition();
  }

  /**
   * 入力値をリセットする
   * @param autoScroll - リストに変更が発生した際に自動スクロールを行うか
   */
  reset(autoScroll?: boolean) {
    this.setValues(createActivitiesFilterModel(), autoScroll);
  }

  /**
   * フィルタフォームのVNodeを生成する
   * @returns
   */
  renderForm(options?: VNodeData) {
    return this.$createElement(HActivitiesFilterForm, options);
  }

  /**
   * 現在絞り込まれたリストにおいてのIDのリストを取得する
   *
   * * 条件変更時のリストの変更検知のために利用する
   *
   * @returns IDのリスト
   */
  private createIds() {
    return this.filteredActivities.map((activity) => activity.id);
  }

  /**
   * 自動スクロールを実行する
   * @returns
   */
  private execAutoScrollToStartPosition() {
    const { autoScrollPosition } = this;
    const el = autoScrollPosition && autoScrollPosition();
    if (!el) return;
    this.$ui.seeElementTop(el as HTMLElement);
  }

  /**
   * 自動スクロールの実行待機を解除する
   */
  private clearAutoScrollToStartPositionTimer() {
    if (this._autoScrollToStartPositionTimerId) {
      clearTimeout(this._autoScrollToStartPositionTimerId);
      delete this._autoScrollToStartPositionTimerId;
    }
  }

  /**
   * 自動スクロールの実行待機を予約する
   *
   * * 連続要求をまとめるための仕組みです
   */
  private reserveAutoScrollToStartPosition() {
    this.clearAutoScrollToStartPositionTimer();
    this._autoScrollToStartPositionTimerId = window.setTimeout(
      this.execAutoScrollToStartPosition,
      250,
    );
  }
}

export const HActivitiesFilterProvider = tsx
  .ofType<
    HActivitiesFilterProviderProps,
    HActivitiesFilterProviderEmits,
    HActivitiesFilterProviderScopedSlots
  >()
  .convert(HActivitiesFilterProviderRef);
